import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Box, Pagination, Card, CardContent, IconButton, Tooltip, Stack, Snackbar, Alert,
  TextField, Checkbox, Button, Chip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ListAltIcon from '@mui/icons-material/ListAlt';
import productsDataRaw from '../products.json';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';
import PageHeader from '../components/ui/PageHeader';
import DataTableCard from '../components/ui/DataTableCard';
import { BarcodeLookupService } from '../services/BarcodeLookupService';
import { ProductService } from '../services/ProductService';

interface Product {
  id: number;
  barkod: string;
  urun_adi: string;
}

const productsData: Product[] = productsDataRaw as Product[];
const PAGE_SIZE = 20;

const ProductsJson: React.FC = () => {
  const [page, setPage] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [copiedValue, setCopiedValue] = useState('');
  const [gotoPage, setGotoPage] = useState<string>('');
  const [gotoError, setGotoError] = useState<string>('');
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [sendSuccess, setSendSuccess] = useState<string>('');
  const [sendError, setSendError] = useState<string>('');
  const [existingBarcodes, setExistingBarcodes] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkSending, setBulkSending] = useState(false);
  const [checkingBarcodes, setCheckingBarcodes] = useState(false);

  const totalProducts = productsData.length;
  const totalPages = Math.ceil(totalProducts / PAGE_SIZE);
  const paginatedProducts = productsData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setSelectedIds([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedValue(value);
    setSnackbarOpen(true);
  };

  const handleGotoPage = () => {
    const num = Number(gotoPage);
    if (!num || num < 1 || num > totalPages) {
      setGotoError(`1 - ${totalPages} arasında bir sayfa girin`);
      return;
    }
    setPage(num);
    setGotoError('');
    setGotoPage('');
    setSelectedIds([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGotoInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleGotoPage();
  };

  const loadBarcodeStatus = useCallback(async (pageNumber: number) => {
    const currentPageProducts = productsData.slice((pageNumber - 1) * PAGE_SIZE, pageNumber * PAGE_SIZE);
    const barcodes = currentPageProducts.map((p) => p.barkod);

    if (barcodes.length === 0) {
      setExistingBarcodes(new Set());
      return;
    }

    setCheckingBarcodes(true);
    try {
      const found = await BarcodeLookupService.checkExistingBarcodes(barcodes);
      setExistingBarcodes(found);
    } finally {
      setCheckingBarcodes(false);
    }
  }, []);

  useEffect(() => {
    loadBarcodeStatus(page);
  }, [page, loadBarcodeStatus]);

  const isSelected = (id: number) => selectedIds.includes(id);
  const handleSelect = (id: number) => {
    setSelectedIds((prev) => (isSelected(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(paginatedProducts.filter((p) => !existingBarcodes.has(p.barkod)).map((p) => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleBulkSend = async () => {
    setBulkSending(true);
    setSendSuccess('');
    setSendError('');

    const items = selectedIds
      .map((id) => paginatedProducts.find((p) => p.id === id))
      .filter((p): p is Product => !!p)
      .map((p) => ({ urun_adi: p.urun_adi, barkod: p.barkod }));

    try {
      const result = await ProductService.addProductsBulk(items);
      setExistingBarcodes((prev) => {
        const next = new Set(prev);
        result.addedBarcodes.forEach((b) => next.add(b));
        return next;
      });
      setSendSuccess(
        `${result.successCount} ürün gönderildi. ${result.alreadyCount} zaten ekli.${result.errorCount > 0 ? ` ${result.errorCount} hata.` : ''}`
      );
      setSelectedIds([]);
    } catch {
      setSendError('Toplu gönderme sırasında hata oluştu.');
    } finally {
      setBulkSending(false);
    }
  };

  const handleSendToFirebase = async (product: Product) => {
    setSendingId(product.id);
    setSendSuccess('');
    setSendError('');

    try {
      const exists = await BarcodeLookupService.isBarcodeExisting(product.barkod);
      if (exists) {
        setSendError('Bu barkod ile ürün zaten mevcut!');
        setExistingBarcodes((prev) => new Set([...Array.from(prev), product.barkod]));
        return;
      }

      const newProduct = await ProductService.addProduct({
        urun_adi: product.urun_adi,
        barkod: product.barkod,
      });
      setSendSuccess(`Ürün başarıyla gönderildi (id: ${newProduct.id})`);
      setExistingBarcodes((prev) => new Set([...Array.from(prev), product.barkod]));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      setSendError('Gönderme sırasında hata oluştu: ' + message);
    } finally {
      setSendingId(null);
    }
  };

  const selectableCount = paginatedProducts.filter((p) => !existingBarcodes.has(p.barkod)).length;

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title="JSON Ürün Listesi"
        subtitle={`Toplam ${totalProducts.toLocaleString('tr-TR')} ürün · Sayfa ${page}/${totalPages}${checkingBarcodes ? ' · Barkodlar kontrol ediliyor...' : ''}`}
        icon={<ListAltIcon />}
        actions={
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            disabled={selectedIds.length === 0 || bulkSending}
            onClick={handleBulkSend}
          >
            {bulkSending ? 'Gönderiliyor...' : `Toplu Gönder (${selectedIds.length})`}
          </Button>
        }
      />

      <Card sx={{ mb: 3, bgcolor: 'primary.light', border: 'none' }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="body2" color="primary.dark" fontWeight={500}>
            Barkod kontrolleri oturum boyunca cache'lenir. Aynı sayfaya döndüğünüzde Firebase'e tekrar istek atılmaz.
          </Typography>
        </CardContent>
      </Card>

      <DataTableCard
        title="Ürün Tablosu"
        subtitle={`Sayfa ${page} — ${paginatedProducts.length} kayıt gösteriliyor`}
        headerAction={
          selectedIds.length > 0 && (
            <Chip label={`${selectedIds.length} seçili`} color="primary" size="small" />
          )
        }
      >
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.length > 0 && selectedIds.length < selectableCount}
                    checked={selectedIds.length > 0 && selectedIds.length === selectableCount && selectableCount > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    inputProps={{ 'aria-label': 'Tümünü seç' }}
                    disabled={checkingBarcodes || bulkSending}
                  />
                </TableCell>
                <TableCell sx={{ width: 60 }}>ID</TableCell>
                <TableCell sx={{ width: 200 }}>Barkod</TableCell>
                <TableCell>Ürün Adı</TableCell>
                <TableCell sx={{ width: 80 }} align="center">Durum</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedProducts.map((product) => {
                const alreadyExists = existingBarcodes.has(product.barkod);
                return (
                  <TableRow key={product.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected(product.id)}
                        onChange={() => handleSelect(product.id)}
                        disabled={alreadyExists || bulkSending || checkingBarcodes}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {product.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={product.barkod}
                        >
                          {product.barkod}
                        </Typography>
                        <Tooltip title="Barkodu kopyala">
                          <IconButton size="small" onClick={() => handleCopy(product.barkod)}>
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          variant="body2"
                          sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={product.urun_adi}
                        >
                          {product.urun_adi}
                        </Typography>
                        {!alreadyExists && (
                          <Tooltip title="Firebase'e gönder">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleSendToFirebase(product)}
                                disabled={sendingId === product.id || checkingBarcodes}
                              >
                                <SendIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      {checkingBarcodes ? (
                        <Chip label="..." size="small" variant="outlined" />
                      ) : alreadyExists ? (
                        <Chip label="Mevcut" size="small" color="success" variant="outlined" />
                      ) : (
                        <Chip label="Yeni" size="small" color="default" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DataTableCard>

      <Box
        display="flex"
        flexDirection="row"
        flexWrap="wrap"
        justifyContent="center"
        alignItems="center"
        mt={4}
        gap={2}
      >
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
          showFirstButton
          showLastButton
        />
        <Stack direction="row" alignItems="flex-start" spacing={0.5}>
          <TextField
            size="small"
            label="Sayfaya Git"
            value={gotoPage}
            onChange={(e) => setGotoPage(e.target.value.replace(/[^0-9]/g, ''))}
            onKeyDown={handleGotoInputKeyDown}
            error={!!gotoError}
            helperText={gotoError}
            sx={{ width: 120 }}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1, max: totalPages }}
          />
          <IconButton color="primary" onClick={handleGotoPage} aria-label="Git" sx={{ mt: 0.5 }}>
            <ArrowForwardIcon />
          </IconButton>
        </Stack>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={1500}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>
          Barkod kopyalandı: {copiedValue}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!sendSuccess}
        autoHideDuration={2500}
        onClose={() => setSendSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>
          {sendSuccess}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!sendError}
        autoHideDuration={3000}
        onClose={() => setSendError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
          {sendError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductsJson;
